import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, STORE_SLUG } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { ARTIFACTS as A, fmtGameDate, fmtGameTime, upcomingGames, seasonRecord } from "../lib/schedule";
import { ArrowRight, Heart, QrCode, Shirt, CalendarDays, Home as HomeIcon, Plane, Snowflake, Mail, Phone, ExternalLink } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/06be898d-4613-437a-a5f0-1a439b0236fd/images/605df0d9de94d33209233fa3ad29e7f3b539a60c0b8b4b57de3c1590822883d1.jpeg";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function OrderTicker() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get(`/stores/${STORE_SLUG}/recent-orders`).then((r) => setOrders(r.data || [])).catch(() => {}); }, []);
  if (!orders.length) return null;
  const row = [...orders, ...orders];
  const badge = (s) => ({ "New": "text-yellow-400", "Printing": "text-sky-400", "Ready for Pickup": "text-green-400", "Shipped": "text-green-400", "Completed": "text-gray-400" }[s] || "text-gray-400");
  return (
    <div className="border-y border-white/10 bg-[#0d0d0f] py-3 overflow-hidden" data-testid="order-ticker">
      <style>{`@keyframes gv-ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}.gv-ticker-track{animation:gv-ticker 45s linear infinite}.gv-ticker-track:hover{animation-play-state:paused}`}</style>
      <div className="flex items-center gap-2 px-6 mb-2">
        <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse" />
        <span className="text-xs uppercase tracking-[0.25em] text-gray-400 font-bold">Live order feed</span>
      </div>
      <div className="relative w-full overflow-hidden">
        <div className="gv-ticker-track flex gap-3 w-max">
          {row.map((o, i) => (
            <div key={i} className="flex items-center gap-3 border border-white/10 bg-[#141518] px-4 py-2 rounded-full whitespace-nowrap" data-testid={`ticker-item-${i}`}>
              <Shirt className="w-4 h-4 text-[var(--gv-blue-bright)]" />
              <span className="text-sm text-white font-semibold">Order {o.order_number}</span>
              <span className="text-sm text-gray-400">{o.summary}</span>
              <span className={`text-xs font-bold ${badge(o.status)}`}>{o.status}</span>
              <span className="text-xs text-gray-600">{timeAgo(o.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
const LOGO = "https://customer-assets-0z36b82j.emergentagent.net/job_gray-spirit-gear/artifacts/qoih72dz_glogo.jpeg";
const DJ_LOGO = "https://customer-assets-0z36b82j.emergentagent.net/job_gray-spirit-gear/artifacts/bn2c9jw5_Untitled%20design%20%2832%29.webp";

const CATS = [
  { slug: "player-gear", label: "Player Gear", desc: "Team tees, personalized" },
  { slug: "parents-family", label: "Parents & Family", desc: "Cheer them on" },
  { slug: "fan-gear", label: "Fan Gear", desc: "Rep the program" },
];

const SHOWCASE = [
  { img: `${A}/jl1v9ldl_12.png`, label: "Black • War Eagles Crest", color: "Black", design: "crest" },
  { img: `${A}/4gtf9uy5_17.png`, label: "Black • 2026-27 Vintage", color: "Black", design: "vintage" },
  { img: `${A}/h3ugjy90_22.png`, label: "Grey • War Eagles Crest", color: "Grey", design: "crest" },
  { img: `${A}/8b9dxg14_26.png`, label: "Grey • 2026-27 Vintage", color: "Grey", design: "vintage" },
  { img: `${A}/4o7j1cjl_6.png`, label: "White • War Eagles Crest", color: "White", design: "crest" },
  { img: `${A}/1w4o0nne_14.png`, label: "White • 2026-27 Vintage", color: "White", design: "vintage" },
  { img: `${A}/es7hwvb1_16.png`, label: "Black • GRAY Volleyball", color: "Black", design: "varsity" },
  { img: `${A}/zrk57zut_9.png`, label: "White • GRAY Volleyball", color: "White", design: "varsity" },
  { img: `${A}/zzdolirz_24.png`, label: "Full-Team Roster Back", color: "Grey", design: "crest" },
  { img: `${A}/x0yr39z0_23.png`, label: "Custom Name & Number", color: "Grey", design: "crest", custom: true },
];

const HOODIE_SHOW = [
  { color: "Black", design: "crest", label: "Black • War Eagles Crest" },
  { color: "Grey", design: "vintage", label: "Grey • 2026-27 Vintage" },
  { color: "White", design: "varsity", label: "White • GRAY Volleyball" },
  { color: "Black", design: "varsity", label: "Black • GRAY Volleyball" },
];

// Promo lookbook graphics (4:5). Add new sets by appending URLs here.
const B = process.env.REACT_APP_BACKEND_URL;
const PROMO_SHOTS = [
  `${B}/api/files/gca-volleyball/lookbook/promo_15_d537409b.jpg`,
  `${B}/api/files/gca-volleyball/lookbook/promo_9_1bfd5d51.jpg`,
  `${B}/api/files/gca-volleyball/lookbook/promo_19_7d36c61f.jpg`,
  `${B}/api/files/gca-volleyball/lookbook/promo_17_ea53e8d6.jpg`,
  `${B}/api/files/gca-volleyball/lookbook/promo_16_462f483b.jpg`,
  `${B}/api/files/gca-volleyball/lookbook/promo_20_aaa1154f.jpg`,
  `${B}/api/files/gca-volleyball/lookbook/promo_11_28b50746.jpg`,
  `${B}/api/files/gca-volleyball/lookbook/promo_10_44a95cb8.jpg`,
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get(`/stores/${STORE_SLUG}`).then((r) => setStore(r.data));
    api.get(`/stores/${STORE_SLUG}/products`, { params: { popular: true } }).then((r) => setProducts(r.data));
  }, []);

  const priceFrom = store?.pricing?.front_only ?? 25;
  const namePrice = store?.pricing?.name_number ?? 33;
  const featured = products[0];
  const link = (c, d, custom) => featured ? `/product/${featured.id}?color=${c}&design=${d}${custom ? "&custom=1" : ""}` : "/shop/shop-all";
  const nextGames = upcomingGames(store?.schedule || [], 3);
  const record = seasonRecord(store?.schedule || []);

  const hoodie = products.find((p) => /hoodie/i.test(p.name));
  const hColor = (name) => hoodie?.colors?.find((c) => c.name === name) || {};
  const hImg = (name, design) => hColor(name).fronts?.[design] || hoodie?.front_image;
  const hLink = (color, design, custom) => hoodie ? `/product/${hoodie.id}?color=${color}&design=${design}${custom ? "&custom=1" : ""}` : "/shop/shop-all";
  const hoodiePrice = hoodie?.pricing?.front_only ?? 40;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b-4 border-[var(--gv-blue)]">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/50" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 sm:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <motion.div className="flex-shrink-0" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <div className="gv-float w-44 h-44 sm:w-56 sm:h-56 rounded-full bg-white flex items-center justify-center shadow-[0_0_60px_rgba(242,183,5,0.35)] ring-4 ring-[var(--gv-blue)]">
              <img src={LOGO} alt="Gray Collegiate Academy War Eagles" className="w-36 h-36 sm:w-48 sm:h-48 object-contain rounded-full" />
            </div>
          </motion.div>
          <motion.div className="text-center lg:text-left" initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.1 } } }}>
            <motion.p variants={fadeUp} className="text-[var(--gv-blue-bright)] uppercase tracking-[0.3em] text-xs font-bold mb-3">Official War Eagles Team Store</motion.p>
            <motion.h1 variants={fadeUp} className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">GRAY COLLEGIATE ACADEMY <br /><span className="text-[var(--gv-blue-bright)]">VOLLEYBALL</span></motion.h1>
            <motion.p variants={fadeUp} className="mt-4 text-gray-200 text-lg tracking-wide">Varsity • JV • Middle School • Family • Fans</motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
              <Link to="/shop/shop-all" data-testid="hero-shop-btn" className="bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 transition-colors flex items-center gap-2">
                Shop Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/schedule" data-testid="hero-schedule-btn" className="border border-white/30 hover:border-[var(--gv-blue-bright)] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 transition-colors flex items-center gap-2">
                <CalendarDays className="w-4 h-4" /> Schedule
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Donation banner */}
      <div className="bg-[var(--gv-blue)] text-black" data-testid="donation-banner">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center gap-3 text-center">
          <Heart className="w-5 h-5 flex-shrink-0" fill="black" />
          <p className="font-bold text-sm sm:text-base">15% of merchandise sales are donated back to the Gray Volleyball program.</p>
        </div>
      </div>

      {/* Winter is Coming — featured hoodie */}
      {hoodie && (
        <section className="max-w-7xl mx-auto px-6 pt-14" data-testid="winter-hoodie-feature">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="relative overflow-hidden border border-sky-400/30 bg-gradient-to-br from-[#0b1622] via-[#0a0f16] to-black grid md:grid-cols-2 items-stretch">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.18),transparent_55%)] pointer-events-none" />
            <Snowflake className="absolute top-6 right-6 w-24 h-24 text-sky-400/10" />
            <div className="relative p-8 sm:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-2 text-sky-300 uppercase tracking-[0.3em] text-xs font-bold mb-3">
                <Snowflake className="w-4 h-4" /> Winter Is Coming
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">STAY WARM IN <span className="text-[var(--gv-blue-bright)]">WAR EAGLES</span> COLORS</h2>
              <p className="mt-4 text-gray-300 text-base max-w-md">Brand-new team hoodies on a Gildan Heavy Blend fleece. Pick your color, front design & add the roster or your player's name & number.</p>
              <p className="mt-3 text-sm text-sky-200/90 font-bold">From ${hoodiePrice}</p>
              <div className="mt-7">
                <Link to={hLink("Black", "crest")} data-testid="winter-shop-hoodies-btn"
                  className="inline-flex items-center gap-2 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 transition-colors">
                  Shop Hoodies <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <Link to={hLink("Black", "crest")} className="relative block group overflow-hidden bg-black min-h-[320px]">
              <img src={hImg("Black", "crest")} alt="War Eagles Volleyball Hoodie" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </Link>
          </motion.div>
        </section>
      )}

      {/* Team hoodies showcase */}
      {hoodie && (
        <section className="max-w-7xl mx-auto px-6 pt-14" data-testid="hoodie-showcase">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="flex items-end justify-between mb-6">
            <div>
              <p className="text-sky-300 uppercase tracking-[0.3em] text-xs font-bold mb-1">New for the Season</p>
              <h2 className="font-display text-4xl sm:text-5xl">TEAM HOODIES</h2>
            </div>
            <Link to={hLink("Black", "crest")} className="text-sm uppercase tracking-widest text-[var(--gv-blue-bright)] font-bold whitespace-nowrap">Shop all</Link>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {HOODIE_SHOW.map((s, i) => (
              <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fadeUp}>
                <Link to={hLink(s.color, s.design)} data-testid={`hoodie-showcase-${i}`} className="gv-lift block group border border-white/10 bg-[#141518] overflow-hidden">
                  <div className="aspect-square overflow-hidden bg-black relative">
                    <span className="absolute top-3 left-3 z-10 bg-sky-500 text-black text-[10px] uppercase tracking-widest font-bold px-2 py-1">Hoodie</span>
                    <img src={hImg(s.color, s.design)} alt={s.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[11px] sm:text-xs font-bold truncate">{s.label}</p>
                    <p className="text-[11px] text-[var(--gv-blue-bright)] font-bold mt-0.5">From ${hoodiePrice}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Live order feed */}
      <OrderTicker />

      {/* Lookbook / promo gallery */}
      {PROMO_SHOTS.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pt-14" data-testid="lookbook">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="flex items-end justify-between mb-6">
            <div>
              <p className="text-[var(--gv-blue-bright)] uppercase tracking-[0.3em] text-xs font-bold mb-1">Team Store Lookbook</p>
              <h2 className="font-display text-4xl sm:text-5xl">FRESH DROPS</h2>
            </div>
            <Link to="/shop/shop-all" className="text-sm uppercase tracking-widest text-[var(--gv-blue-bright)] font-bold whitespace-nowrap">Shop all</Link>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {PROMO_SHOTS.map((src, i) => (
              <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fadeUp}>
                <Link to="/shop/shop-all" data-testid={`lookbook-${i}`} className="gv-lift block group border border-white/10 bg-black overflow-hidden">
                  <div className="aspect-[4/5] overflow-hidden bg-black">
                    <img src={src} alt="Gray Volleyball gear" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Product showcase gallery */}
      <section className="max-w-7xl mx-auto px-6 pt-14">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="flex items-end justify-between mb-6">
          <div>
            <p className="text-[var(--gv-blue-bright)] uppercase tracking-[0.3em] text-xs font-bold mb-1">The Lineup</p>
            <h2 className="font-display text-4xl sm:text-5xl">SHOP THE GEAR</h2>
          </div>
          <Link to="/shop/shop-all" className="text-sm uppercase tracking-widest text-[var(--gv-blue-bright)] font-bold whitespace-nowrap">Build yours</Link>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SHOWCASE.map((s, i) => (
            <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fadeUp}>
              <Link to={link(s.color, s.design, s.custom)} data-testid={`showcase-${i}`} className="gv-lift block group border border-white/10 bg-[#141518] overflow-hidden">
                <div className="aspect-square overflow-hidden bg-black">
                  <img src={s.img} alt={s.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <p className="text-[11px] sm:text-xs font-bold px-3 py-2 truncate">{s.label}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Custom player number advert */}
      <section className="max-w-7xl mx-auto px-6 pt-14">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          data-testid="custom-number-promo" className="relative overflow-hidden border border-[var(--gv-blue)]/40 bg-gradient-to-r from-[#141518] to-black p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6">
          <div className="gv-float w-20 h-20 rounded-full bg-[var(--gv-blue)] flex items-center justify-center flex-shrink-0 shadow-[0_0_40px_rgba(242,183,5,0.35)]">
            <Shirt className="w-10 h-10 text-black" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p className="text-[var(--gv-blue-bright)] uppercase tracking-[0.3em] text-xs font-bold mb-1">Make It Yours</p>
            <h3 className="font-display text-3xl sm:text-4xl leading-none">CUSTOM PLAYER NUMBER TEES</h3>
            <p className="mt-2 text-gray-300 text-sm">Put your player's name &amp; number on the back for <span className="text-white font-bold">${namePrice}</span>. Add your details right at checkout.</p>
          </div>
          <Link to={featured ? `/product/${featured.id}?custom=1` : "/shop/shop-all"} data-testid="custom-number-cta"
            className="bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 transition-colors flex items-center gap-2 flex-shrink-0">
            Customize <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </section>

      {/* Schedule & results */}
      {(nextGames.length > 0 || store?.schedule_image) && (
        <section className="max-w-7xl mx-auto px-6 pt-14" data-testid="upcoming-games">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div className="flex items-center gap-5">
              <h2 className="font-display text-4xl sm:text-5xl">SCHEDULE &amp; RESULTS</h2>
              {(record.wins > 0 || record.losses > 0) && (
                <div data-testid="season-record" className="flex items-center gap-1 font-display text-3xl sm:text-4xl leading-none">
                  <span className="text-green-400">{record.wins}</span>
                  <span className="text-gray-500">-</span>
                  <span className="text-red-400">{record.losses}</span>
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-gray-500 font-sans">W-L</span>
                </div>
              )}
            </div>
            <Link to="/schedule" className="text-sm uppercase tracking-widest text-[var(--gv-blue-bright)] font-bold">Full schedule</Link>
          </div>
          {nextGames.length > 0 && (
            <div className="grid sm:grid-cols-3 gap-3">
              {nextGames.map((g, i) => {
                const home = (g.location || "").toUpperCase() === "HOME";
                const r = (g.result || "").trim().toUpperCase();
                const isWin = r.startsWith("W");
                const isLoss = r.startsWith("L");
                return (
                  <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                    data-testid={`home-game-${i}`}
                    className="gv-lift border border-white/10 bg-[#141518] p-5 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[var(--gv-blue-bright)] font-display text-2xl leading-none">{fmtGameDate(g.datetime)}</p>
                      <p className="text-xs text-gray-400 mb-3">{fmtGameTime(g.datetime)}</p>
                      <p className="font-bold text-sm truncate">{g.opponent}</p>
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">{home ? <HomeIcon className="w-3 h-3" /> : <Plane className="w-3 h-3" />} {home ? "Home" : "Away"}</p>
                      {g.score && <p className="text-xs text-gray-500 mt-1">{g.score}</p>}
                    </div>
                    {(isWin || isLoss) && (
                      <span data-testid={`home-game-result-${i}`}
                        className={`flex-shrink-0 font-display text-5xl leading-none ${isWin ? "text-green-400" : "text-red-400"}`}>
                        {isWin ? "W" : "L"}
                      </span>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
          {store?.schedule_image && (
            <div className="mt-6 flex justify-center">
              <Link to="/schedule" data-testid="home-schedule-image"
                className="block max-w-sm w-full border border-white/10 rounded-lg overflow-hidden hover:border-[var(--gv-blue)]/50 transition-colors">
                <img src={store.schedule_image} alt="2026 War Eagles Volleyball Schedule" className="w-full h-auto" />
                <p className="text-center text-[11px] uppercase tracking-widest text-gray-400 py-2 bg-[#141518]">2026 Full Schedule</p>
              </Link>
            </div>
          )}
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-display text-4xl sm:text-5xl mb-8">SHOP BY CATEGORY</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {CATS.map((c, i) => (
            <motion.div key={c.slug} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
              <Link to={`/shop/${c.slug}`} data-testid={`cat-tile-${c.slug}`}
                className="gv-lift block group border border-white/10 hover:border-[var(--gv-blue)] bg-[#141518] p-8 transition-colors h-full">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{c.desc}</p>
                <h3 className="font-display text-3xl mt-2 group-hover:text-[var(--gv-blue-bright)] transition-colors">{c.label}</h3>
                <ArrowRight className="w-5 h-5 mt-4 text-[var(--gv-blue-bright)] group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Popular */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display text-4xl sm:text-5xl">MOST POPULAR</h2>
          <Link to="/shop/shop-all" className="text-sm uppercase tracking-widest text-[var(--gv-blue-bright)] font-bold">View all</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {products.map((p) => <ProductCard key={p.id} product={p} priceFrom={p.pricing?.front_only ?? priceFrom} />)}
        </div>
      </section>

      {/* Dad Jerzeys advertisement */}
      <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="dadjerzeys-ad">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="relative overflow-hidden border border-[var(--gv-blue)]/40 bg-gradient-to-r from-[#141518] to-black p-8 sm:p-10">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <a href="https://www.dadjerzeys.com" target="_blank" rel="noopener noreferrer" data-testid="ad-dadjerzeys-logo" className="gv-float bg-white rounded-2xl p-4 flex items-center justify-center flex-shrink-0 shadow-[0_0_40px_rgba(0,0,0,0.4)]">
              <img src={DJ_LOGO} alt="Dad Jerzeys" className="w-32 h-24 object-contain" />
            </a>
            <div className="flex-1 text-center md:text-left">
              <p className="text-[var(--gv-blue-bright)] uppercase tracking-[0.3em] text-xs font-bold mb-1">Part of the Dad Jerzeys Family</p>
              <h3 className="font-display text-3xl sm:text-4xl leading-none">CUSTOM PARENT SPORTS JERSEYS</h3>
              <p className="mt-2 text-gray-300 text-sm">This War Eagles store is powered by <span className="text-white font-bold">Dad Jerzeys</span> — custom name &amp; number jerseys for any team, sport or family. Need gear for your program? Let's make it.</p>
              <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-x-6 gap-y-2 items-center md:items-start justify-center md:justify-start text-sm text-gray-300">
                <a href="mailto:dadjerzeys@gmail.com" data-testid="ad-email-link" className="flex items-center gap-2 hover:text-[var(--gv-blue-bright)] transition-colors">
                  <Mail className="w-4 h-4" /> dadjerzeys@gmail.com
                </a>
                <a href="tel:8038302211" data-testid="ad-phone-link" className="flex items-center gap-2 hover:text-[var(--gv-blue-bright)] transition-colors">
                  <Phone className="w-4 h-4" /> (803) 830-2211
                </a>
              </div>
            </div>
            <a href="https://www.dadjerzeys.com" target="_blank" rel="noopener noreferrer" data-testid="dadjerzeys-visit-btn"
              className="bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 transition-colors flex items-center gap-2 flex-shrink-0">
              Visit Dad Jerzeys <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
